import axios from 'axios';

const useLocalApiProxy = String(import.meta.env.VITE_USE_LOCAL_API ?? '').toLowerCase() === 'true';
const apiBaseUrl = useLocalApiProxy ? '' : (import.meta.env.VITE_SCIO_API_URL || '');

const http = axios.create({
    baseURL: apiBaseUrl,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true
});

const SAFE_METHODS = new Set(['get', 'head', 'options', 'trace']);
let csrfToken = null;
let csrfTokenRequest = null;
let authExpiryNotified = false;

async function getCsrfToken() {
    if (csrfToken) return csrfToken;

    if (!csrfTokenRequest) {
        csrfTokenRequest = http.get('/api/security/csrf-token', {
            _skipCsrf: true
        }).then(({ data }) => {
            if (!data?.token) {
                throw new Error('The API did not return a CSRF token.');
            }

            csrfToken = data.token;
            return csrfToken;
        }).finally(() => {
            csrfTokenRequest = null;
        });
    }

    return csrfTokenRequest;
}

// Authentication remains in HttpOnly cookies. Unsafe requests also carry a
// synchronizer token so cross-site pages cannot submit authenticated changes.
http.interceptors.request.use(
    async (config) => {
        const method = (config.method || 'get').toLowerCase();
        if (!config._skipCsrf && !SAFE_METHODS.has(method)) {
            config.headers.set('X-CSRF-TOKEN', await getCsrfToken());
        }
        return config;
    },
    (error) => Promise.reject(error)
);

http.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            if (!authExpiryNotified) {
                authExpiryNotified = true;
                const currentHash = window.location.hash || '#/programmes/';
                const currentPath = currentHash.startsWith('#')
                    ? currentHash.slice(1)
                    : '/programmes/';

                window.dispatchEvent(new CustomEvent('scio:auth-expired'));

                if (!currentHash.startsWith('#/login/')) {
                    window.location.hash = `#/login/?reason=session-expired&redirect=${encodeURIComponent(currentPath)}`;
                }
            }
        }
        return Promise.reject(error);
    }
);

export default http;
